import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const { loginWithGoogle, accessDenied, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  async function handleLogin() {
    setLoading(true);
    setError("");
    try {
      await loginWithGoogle();
      navigate("/dashboard");
    } catch (err) {
      setError("Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.container}>
      {/* Background grid */}
      <div style={styles.grid} />
      <div style={styles.glow} />

      <div style={styles.card} className="animate-in">
        {/* Logo mark */}
        <div style={styles.logoWrap}>
          <div style={styles.logo}>
            <span style={styles.logoIcon}>◈</span>
          </div>
        </div>

        <h1 style={styles.title}>FTPS</h1>
        <p style={styles.subtitle}>Partner Stake Tracker</p>
        <p style={styles.desc}>
          Secure access for authorized partners only.<br />
          Sign in with your approved Google account.
        </p>

        {accessDenied && (
          <div style={styles.errorBox}>
            <span style={styles.errorIcon}>⚠</span>
            <div>
              <div style={styles.errorTitle}>Access Denied</div>
              <div style={styles.errorMsg}>Your email is not on the approved list. Contact an admin.</div>
              <button onClick={logout} style={styles.switchBtn}>Try a different account</button>
            </div>
          </div>
        )}

        {error && (
          <div style={styles.errorBox}>
            <span style={styles.errorIcon}>⚠</span>
            <div style={styles.errorMsg}>{error}</div>
          </div>
        )}

        <button onClick={handleLogin} disabled={loading} style={styles.googleBtn}>
          {loading ? (
            <span style={styles.spinner} />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          {loading ? "Signing in..." : "Continue with Google"}
        </button>

        <p style={styles.footer}>
          Access is restricted to approved partners.<br />
          All activity is logged and monitored.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg-primary)",
    position: "relative",
    overflow: "hidden",
    padding: "24px",
  },
  grid: {
    position: "absolute",
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(108,99,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(108,99,255,0.04) 1px, transparent 1px)
    `,
    backgroundSize: "60px 60px",
  },
  glow: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "600px",
    height: "600px",
    background: "radial-gradient(circle, rgba(108,99,255,0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-xl)",
    padding: "48px 40px",
    width: "100%",
    maxWidth: "420px",
    position: "relative",
    boxShadow: "var(--shadow-card), var(--shadow-glow)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  logoWrap: {
    marginBottom: "24px",
  },
  logo: {
    width: "64px",
    height: "64px",
    background: "var(--accent-dim)",
    border: "1px solid var(--border-accent)",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
  },
  logoIcon: {
    fontSize: "28px",
    color: "var(--accent-bright)",
  },
  title: {
    fontSize: "32px",
    fontFamily: "var(--font-display)",
    fontWeight: 800,
    letterSpacing: "0.1em",
    color: "var(--text-primary)",
    marginBottom: "4px",
  },
  subtitle: {
    fontSize: "13px",
    color: "var(--accent-bright)",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    fontWeight: 600,
    marginBottom: "20px",
  },
  desc: {
    color: "var(--text-secondary)",
    fontSize: "14px",
    lineHeight: 1.7,
    marginBottom: "28px",
  },
  errorBox: {
    width: "100%",
    background: "var(--red-dim)",
    border: "1px solid rgba(255,92,108,0.25)",
    borderRadius: "var(--radius-sm)",
    padding: "12px 16px",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    textAlign: "left",
    marginBottom: "20px",
  },
  errorIcon: {
    color: "var(--red)",
    fontSize: "16px",
    flexShrink: 0,
    marginTop: "1px",
  },
  errorTitle: {
    color: "var(--red)",
    fontWeight: 700,
    fontSize: "13px",
    marginBottom: "2px",
  },
  errorMsg: {
    color: "var(--text-secondary)",
    fontSize: "13px",
  },
  switchBtn: {
    marginTop: "8px",
    background: "none",
    border: "none",
    color: "var(--accent-bright)",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  googleBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    background: "white",
    color: "#1a1a2e",
    padding: "13px 24px",
    borderRadius: "var(--radius-sm)",
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: "var(--font-body)",
    cursor: "pointer",
    border: "none",
    boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
    transition: "var(--transition)",
    marginBottom: "24px",
  },
  spinner: {
    width: "18px",
    height: "18px",
    border: "2px solid rgba(0,0,0,0.15)",
    borderTop: "2px solid #1a1a2e",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    display: "inline-block",
  },
  footer: {
    color: "var(--text-muted)",
    fontSize: "12px",
    lineHeight: 1.7,
  },
};
